import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, ChevronRight } from "lucide-react";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { PublicShell, SignupCta, useSeo } from "@/pages/glossary/PublicShell";

const CAT_LABELS = {
  funding: "Funding", "school-supports": "School Supports", "autism-services": "Autism Services",
  "therapy-navigation": "Therapy Navigation", "parent-guides": "Parent Guides", "canadian-programs": "Canadian Programs",
  neurodiversity: "Neurodiversity", "diagnosis-assessment": "Diagnosis & Assessment", "ieps-education": "IEPs & Education",
  "family-support": "Family Support", "glossary-explainers": "Glossary Explainers", "provincial-guides": "Provincial Guides",
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "");

const CTA_COPY = {
  soft: { text: "Trying to understand what supports may be available for your child? KidPath helps families explore programs, services, and funding options in one guided place.", btn: "Start with KidPath" },
  funding: { text: "Funding rules and programs can feel hard to navigate. KidPath helps families organize what they may need, what to look for, and where to start.", btn: "Explore Support Options" },
  newsletter: { text: "Get parent-friendly guides and updates from KidPath.", btn: "Join the Newsletter" },
};

export default function ArticleDetail({ preview = false }) {
  const { slug, id } = useParams();
  const [a, setA] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        if (preview) {
          const { data } = await api.get(`/admin/articles/${id}`);
          setA(data);
        } else {
          const { data } = await api.get(`/articles/${slug}`);
          setA(data);
          api.get(`/articles/${slug}/related`).then((r) => setRelated(r.data.items)).catch(() => {});
        }
      } catch (e) { setErr("Article not found."); }
    })();
  }, [slug, id, preview]);

  const jsonLd = a ? {
    "@context": "https://schema.org", "@type": "Article", headline: a.title,
    description: a.meta_description || a.excerpt, datePublished: a.published_at, dateModified: a.updated_at,
    author: { "@type": "Organization", name: a.author_name || "KidPath Editorial Team" },
    publisher: { "@type": "Organization", name: "KidPath", logo: { "@type": "ImageObject", url: "https://kidpath.ca/assets/kidpath-mark-dark.png" } },
    image: a.header_image_url ? [a.header_image_url] : undefined,
    mainEntityOfPage: `https://kidpath.ca/articles/${a.slug}`,
    ...(a.faqs && a.faqs.length ? {} : {}),
  } : null;

  useSeo({
    title: a ? `${a.meta_title || a.title} | KidPath` : "Article | KidPath",
    description: a?.meta_description || a?.excerpt || "",
    jsonLd,
    noindex: preview,
  });

  if (err) return <PublicShell><div className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="article-not-found">
    <h1 className="font-display text-3xl font-extrabold text-kid-navy">Article not found</h1>
    <Link to="/articles" className="text-kid-teal font-semibold mt-4 inline-block">← Back to all articles</Link></div></PublicShell>;
  if (!a) return <PublicShell><div className="max-w-2xl mx-auto px-5 py-24 text-center text-kid-muted" data-testid="article-loading">Loading…</div></PublicShell>;

  const cta = CTA_COPY[a.cta_type] || CTA_COPY.soft;

  return (
    <PublicShell>
      {preview && <div className="bg-kid-sun/20 text-[#8a6100] text-center text-sm py-2 font-semibold" data-testid="article-preview-banner">Preview mode — this is how the article will appear once published.</div>}
      <article className="max-w-[1180px] mx-auto px-5 md:px-[30px] py-10 sm:py-14" data-testid="article-detail">
        <nav className="flex items-center gap-1.5 text-xs text-kid-muted mb-5" data-testid="article-breadcrumb">
          <Link to="/" className="hover:text-kid-teal">Home</Link><ChevronRight size={13} />
          <Link to="/articles" className="hover:text-kid-teal">Articles</Link><ChevronRight size={13} />
          <span className="text-kid-navy truncate">{CAT_LABELS[a.category_id] || "Article"}</span>
        </nav>
        <span className="text-sm font-semibold uppercase tracking-wide text-kid-tealcta">{CAT_LABELS[a.category_id] || "Article"}</span>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-kid-navy mt-2 leading-tight" data-testid="article-title">{a.title}</h1>
        {a.subtitle && <p className="text-lg text-kid-muted mt-3">{a.subtitle}</p>}
        <div className="flex flex-wrap items-center gap-3 text-sm text-kid-muted mt-5 pb-6 border-b border-kid-line">
          <span className="font-medium text-kid-navy">{a.author_name || "KidPath Editorial Team"}</span>
          <span>·</span><span>Updated {fmtDate(a.updated_at)}</span>
          {a.read_time_minutes ? <><span>·</span><span className="inline-flex items-center gap-1"><Clock size={13} /> {a.read_time_minutes} min read</span></> : null}
        </div>

        {a.header_image_url && (
          <img src={a.header_image_url} alt={a.header_image_alt || ""} data-testid="article-header-image"
            className="w-full aspect-[16/9] object-cover rounded-2xl mt-7" />
        )}

        <div className="article-body mt-8" data-testid="article-body" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(a.body_html || "") }} />

        {a.cta_type && (
          <div className="my-10 rounded-2xl bg-kid-skytint/50 border border-kid-line p-6 text-center" data-testid="article-cta">
            <p className="text-kid-navy font-medium">{cta.text}</p>
            <Link to="/register" className="inline-flex items-center gap-1.5 rounded-full bg-kid-tealcta text-white text-sm font-semibold px-6 py-3 mt-4 hover:-translate-y-0.5 transition-transform" data-testid="article-cta-btn">{cta.btn} →</Link>
          </div>
        )}

        {a.faqs && a.faqs.length > 0 && (
          <section className="mt-12" data-testid="article-faq">
            <h2 className="font-display text-2xl font-extrabold text-kid-navy mb-4">Frequently asked questions</h2>
            <div className="space-y-3">
              {a.faqs.map((f, i) => (
                <details key={i} className="rounded-xl border border-kid-line p-4" data-testid={`article-faq-${i}`}>
                  <summary className="font-semibold text-kid-navy cursor-pointer">{f.question}</summary>
                  <p className="text-sm text-kid-muted mt-2">{f.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {a.related_links && a.related_links.length > 0 && (
          <section className="mt-10" data-testid="article-related-terms">
            <h2 className="font-display text-xl font-extrabold text-kid-navy mb-3">Related terms & guides</h2>
            <div className="flex flex-wrap gap-2">
              {a.related_links.map((l, i) => (
                <Link key={i} to={l.url || (l.type === "glossary" ? `/terms/${l.target}` : `/articles/${l.target}`)}
                  className="text-sm font-semibold rounded-full px-4 py-2 border border-kid-line text-kid-navy hover:border-kid-teal hover:text-kid-teal transition-colors">{l.anchor || l.title}</Link>
              ))}
            </div>
          </section>
        )}

        {a.show_disclaimer && (
          <p className="mt-10 text-xs text-kid-muted bg-kid-mist rounded-xl p-4 border border-kid-line" data-testid="article-disclaimer">
            This article is for general information only and is not medical, legal, financial, or diagnostic advice. Program rules, funding availability, and eligibility requirements can change. Families should confirm details with the relevant provider, school board, government program, or qualified professional.
          </p>
        )}
      </article>

      {!preview && related.length > 0 && (
        <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] pb-12" data-testid="article-related">
          <h2 className="font-display text-2xl font-extrabold text-kid-navy mb-5">More helpful reads</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.id} to={`/articles/${r.slug}`} className="block rounded-2xl border border-kid-line bg-white overflow-hidden hover:-translate-y-1 transition-transform" data-testid={`related-article-${r.slug}`}>
                <div className="aspect-[16/9] bg-kid-skytint/50 overflow-hidden">{r.header_image_url && <img src={r.header_image_url} alt={r.header_image_alt || ""} loading="lazy" className="w-full h-full object-cover" />}</div>
                <div className="p-4"><span className="text-xs font-semibold uppercase text-kid-tealcta">{CAT_LABELS[r.category_id]}</span><h3 className="font-display font-bold text-kid-navy mt-1 leading-snug">{r.title}</h3></div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-[1180px] mx-auto px-5 md:px-[30px] pb-16"><SignupCta /></div>
    </PublicShell>
  );
}
